import { friendlyDbError } from '../utils/friendlyDbError';
/**
 * Unified Order Store
 * Manages both WooCommerce and Manual orders through the v_all_orders view
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import type {
  UnifiedOrder,
  UnifiedOrderFilters,
  UnifiedOrderStats,
  OrderChannel
} from '../types/orders';
import { ManualOrderService } from '../services/manualOrderService';
import {
  assigneeClause,
  orderSearchFilter,
  selectedStatuses,
  REALTIME_REFRESH_DEBOUNCE_MS,
  isStoreChannel,
} from '../utils/unifiedOrderQuery';
import {
  wooLineItemsToOrderItems,
  wooLookupKeys,
  type WarehouseProductRef,
  type WooLineItemForDuplicate,
} from '../utils/wooOrderDuplicate';

/** Esito di "Duplica ordine": id del nuovo ordine oppure il motivo per cui non si e' potuto */
export interface DuplicateOrderResult {
  id: string | null;
  error?: string;
}

interface UnifiedOrderStore {
  // State
  orders: UnifiedOrder[];
  currentOrder: UnifiedOrder | null;
  stats: UnifiedOrderStats | null;
  loading: boolean;
  error: string | null;
  filters: UnifiedOrderFilters;

  // Pagination
  page: number;
  pageSize: number;
  hasMore: boolean;
  totalCount: number;

  // Ordinamento lato server: l'elenco e' a pagine, ordinare in memoria una
  // pagina sola darebbe un "per cliente" valido su 50 righe e falso sul resto.
  sort: UnifiedOrderSort;

  // Actions
  fetchOrders: (organizationId: string, filters?: UnifiedOrderFilters, opts?: UnifiedOrderFetchOptions) => Promise<void>;
  fetchOrderById: (organizationId: string, orderId: string, channel: OrderChannel) => Promise<void>;
  fetchStats: (organizationId: string, storeId?: string) => Promise<void>;

  // Filters
  setFilters: (filters: UnifiedOrderFilters) => void;
  clearFilters: () => void;

  // Pagination
  setPage: (page: number) => void;

  // Quick Reorder (duplicate)
  duplicateOrder: (orderId: string, organizationId: string, userId: string) => Promise<DuplicateOrderResult>;

  // Assignee management
  updateOrderAssignee: (orderId: string, channel: OrderChannel, assigneeId: string | null, organizationId: string) => Promise<boolean>;

  // Real-time subscription
  subscribeToOrders: (organizationId: string) => () => void;

  // Reset
  reset: () => void;
  resetCurrentOrder: () => void;
}

export type UnifiedOrderSortColumn =
  | 'order_number' | 'status' | 'customer_name' | 'item_count' | 'created_at' | 'order_date' | 'total' | 'channel';

export interface UnifiedOrderSort {
  column: UnifiedOrderSortColumn;
  ascending: boolean;
}

export interface UnifiedOrderFetchOptions {
  page?: number;
  pageSize?: number;
  sort?: UnifiedOrderSort;
}

// Data dell'ordine (per i negozi quella del negozio, anche se cambiata dopo), non quella della riga in Flusia
export const DEFAULT_ORDER_SORT: UnifiedOrderSort = { column: 'order_date', ascending: false };
const DEFAULT_SORT = DEFAULT_ORDER_SORT;

const initialState = {
  orders: [],
  currentOrder: null,
  stats: null,
  loading: false,
  error: null,
  filters: { channel: 'all' as const },
  page: 0,
  pageSize: 50,
  hasMore: true,
  totalCount: 0,
  sort: DEFAULT_SORT,
};

/**
 * Cliente e assegnatario di una pagina di ordini in due query, non due per
 * riga: con 200 righe per pagina il vecchio ciclo faceva 400 chiamate.
 */
async function enrichOrders(rows: any[]): Promise<UnifiedOrder[]> {
  if (rows.length === 0) return [];
  const clientIds = [...new Set(rows.map(r => r.client_id).filter(Boolean))] as string[];
  const assigneeIds = [...new Set(rows.map(r => r.assigned_to).filter(Boolean))] as string[];

  const [clientsRes, assigneesRes] = await Promise.all([
    clientIds.length
      ? supabase.from('clients').select('id, business_name, email, vat_number').in('id', clientIds)
      : Promise.resolve({ data: [] as any[] }),
    assigneeIds.length
      ? supabase.from('user_profiles').select('id, full_name, email, avatar_url').in('id', assigneeIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);

  const clients = new Map((clientsRes.data || []).map((c: any) => [c.id, c]));
  const assignees = new Map((assigneesRes.data || []).map((u: any) => [u.id, u]));

  return rows.map(order => ({
    ...order,
    client: order.client_id ? clients.get(order.client_id) ?? null : undefined,
    assignee: order.assigned_to ? assignees.get(order.assigned_to) ?? null : undefined,
  })) as UnifiedOrder[];
}

export const useUnifiedOrderStore = create<UnifiedOrderStore>((set, get) => ({
  ...initialState,

  // =====================================================
  // FETCH ORDERS (from v_all_orders view)
  // =====================================================

  fetchOrders: async (organizationId: string, filters?: UnifiedOrderFilters, opts?: UnifiedOrderFetchOptions) => {
    try {
      set({ loading: true, error: null });

      const appliedFilters = filters || get().filters;
      const page = opts?.page ?? 0;
      const pageSize = opts?.pageSize ?? get().pageSize;
      const sort = opts?.sort ?? get().sort;
      const offset = page * pageSize;

      let query = supabase
        .from('v_all_orders')
        .select('*', { count: 'exact' })
        .eq('organization_id', organizationId)
        // created_at come secondo criterio: a parita' (stesso stato, stesso
        // cliente) l'ordine resta stabile tra una pagina e l'altra
        .order(sort.column, { ascending: sort.ascending, nullsFirst: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + pageSize - 1);

      // Apply filters (Stato e Operatore a scelta multipla: tutti i valori, non solo il primo)
      const statuses = selectedStatuses(appliedFilters);
      if (statuses.length === 1) {
        query = query.eq('status', statuses[0]);
      } else if (statuses.length > 1) {
        query = query.in('status', statuses);
      }

      if (appliedFilters.channel && appliedFilters.channel !== 'all') {
        query = query.eq('channel', appliedFilters.channel);
      }

      if (appliedFilters.clientId) {
        query = query.eq('client_id', appliedFilters.clientId);
      }

      if (appliedFilters.storeId) {
        query = query.eq('commerce_store_id', appliedFilters.storeId);
      }

      // Testo protetto tra apici: virgole e parentesi non rompono piu' la query
      const searchFilter = orderSearchFilter(appliedFilters.search);
      if (searchFilter) {
        query = query.or(searchFilter);
      }

      if (appliedFilters.dateFrom) {
        query = query.gte('order_date', appliedFilters.dateFrom);
      }

      if (appliedFilters.dateTo) {
        query = query.lte('order_date', appliedFilters.dateTo);
      }

      const assignee = assigneeClause(appliedFilters);
      if (assignee.type === 'unassigned') {
        query = query.is('assigned_to', null);
      } else if (assignee.type === 'in') {
        query = assignee.ids.length === 1
          ? query.eq('assigned_to', assignee.ids[0])
          : query.in('assigned_to', assignee.ids);
      } else if (assignee.type === 'or') {
        query = query.or(assignee.expr);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      const ordersWithRelations = await enrichOrders(data || []);

      set({
        orders: ordersWithRelations,
        totalCount: count || 0,
        loading: false,
        page,
        pageSize,
        sort,
        hasMore: offset + (data?.length || 0) < (count || 0),
        filters: appliedFilters,
        error: null
      });

      // Also fetch stats
      get().fetchStats(organizationId, appliedFilters.storeId);

    } catch (error) {
      console.error('Error fetching unified orders:', error);
      set({
        error: friendlyDbError(error) || 'Errore caricamento ordini',
        loading: false
      });
    }
  },

  fetchOrderById: async (organizationId: string, orderId: string, channel: OrderChannel) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('v_all_orders')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('id', orderId)
        .single();

      if (error) throw error;

      if (!data) {
        set({
          error: 'Ordine non trovato',
          loading: false,
          currentOrder: null
        });
        return;
      }

      // Fetch client info if available
      let client = null;
      if (data.client_id) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('id, business_name, email, vat_number')
          .eq('id', data.client_id)
          .single();
        client = clientData;
      }

      set({
        currentOrder: { ...data, client } as UnifiedOrder,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Error fetching order:', error);
      set({
        error: friendlyDbError(error) || 'Errore caricamento ordine',
        loading: false
      });
    }
  },

  // =====================================================
  // FETCH STATS
  // =====================================================

  fetchStats: async (organizationId: string, storeId?: string) => {
    try {
      let q = supabase
        .from('v_all_orders')
        .select('status, channel')
        .eq('organization_id', organizationId);
      if (storeId) q = q.eq('commerce_store_id', storeId);
      const { data, error } = await q;

      if (error) throw error;

      const orders = data || [];

      // Calculate stats
      const byStatus: Record<string, number> = {};
      orders.forEach(order => {
        byStatus[order.status] = (byStatus[order.status] || 0) + 1;
      });

      const stats: UnifiedOrderStats = {
        total: orders.length,
        // "Negozio" = tutti gli ordini dai negozi online (WooCommerce e Shopify), non solo WooCommerce
        woocommerce: orders.filter(o => isStoreChannel(o.channel)).length,
        manual: orders.filter(o => o.channel === 'manual').length,
        byStatus
      };

      set({ stats });

    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
  },

  // =====================================================
  // FILTERS
  // =====================================================

  setFilters: (filters: UnifiedOrderFilters) => {
    set({ filters });
  },

  clearFilters: () => {
    set({ filters: { channel: 'all' } });
  },

  // =====================================================
  // PAGINATION
  // =====================================================

  setPage: (page: number) => {
    set({ page });
  },

  // =====================================================
  // DUPLICATE ORDER (Quick Reorder)
  // =====================================================

  duplicateOrder: async (orderId: string, organizationId: string, userId: string) => {
    try {
      // Si legge SEMPRE l'ordine cliccato. Prima si usava currentOrder se era
      // gia' in memoria: dopo il primo "Duplica" ogni duplicazione successiva
      // ripartiva dal primo ordine (canale e righe sbagliati).
      const { data: source, error: sourceError } = await supabase
        .from('v_all_orders')
        .select('id, channel, client_id, raw_data')
        .eq('organization_id', organizationId)
        .eq('id', orderId)
        .maybeSingle();

      if (sourceError) throw sourceError;
      if (!source) return { id: null, error: 'Ordine non trovato' };

      if (source.channel === 'manual') {
        const newOrder = await ManualOrderService.duplicateOrder(orderId, {
          organizationId,
          userId
        });
        return { id: newOrder.id };
      }

      // Ordine WooCommerce: diventa un ordine manuale con le stesse righe,
      // agganciate ai prodotti del magazzino (non all'id WooCommerce).
      if (!source.client_id) {
        return { id: null, error: 'Collega prima l\'ordine a un cliente, poi duplicalo.' };
      }
      const rawData = (source.raw_data || {}) as Record<string, unknown>;
      const lineItems = (rawData.line_items || []) as WooLineItemForDuplicate[];
      if (lineItems.length === 0) {
        return { id: null, error: 'L\'ordine non ha righe da duplicare.' };
      }

      const { wooIds, skus } = wooLookupKeys(lineItems);
      const products: WarehouseProductRef[] = [];
      if (wooIds.length > 0) {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, sku, woocommerce_product_id')
          .eq('organization_id', organizationId)
          .in('woocommerce_product_id', wooIds);
        if (error) throw error;
        products.push(...((data || []) as WarehouseProductRef[]));
      }
      if (skus.length > 0) {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, sku, woocommerce_product_id')
          .eq('organization_id', organizationId)
          .in('sku', skus);
        if (error) throw error;
        products.push(...((data || []) as WarehouseProductRef[]));
      }

      const { items, missing } = wooLineItemsToOrderItems(lineItems, products);
      if (missing.length > 0) {
        return {
          id: null,
          error: `Prodotti non trovati in magazzino: ${missing.join(', ')}. Importali o collegali prima di duplicare l'ordine.`,
        };
      }

      const newOrder = await ManualOrderService.createOrder({
        client_id: source.client_id,
        items,
        auto_confirm: false
      }, {
        organizationId,
        userId
      });

      return { id: newOrder.id };

    } catch (error) {
      console.error('Error duplicating order:', error);
      return { id: null, error: friendlyDbError(error) || 'Duplicazione non riuscita' };
    }
  },

  // =====================================================
  // ASSIGNEE MANAGEMENT
  // =====================================================

  updateOrderAssignee: async (orderId: string, channel: OrderChannel, assigneeId: string | null, organizationId: string) => {
    try {
      const table = channel === 'woocommerce' ? 'woocommerce_orders' : 'orders';

      const { error } = await supabase
        .from(table)
        .update({ assigned_to: assigneeId })
        .eq('id', orderId);

      if (error) throw error;

      // Update local state
      const { orders } = get();
      const updatedOrders = orders.map(order => {
        if (order.id === orderId) {
          return { ...order, assigned_to: assigneeId };
        }
        return order;
      });
      set({ orders: updatedOrders });

      // Refresh to get updated assignee info, restando sulla pagina e
      // sull'ordinamento correnti (prima tornava a pagina 1)
      const { filters, page, pageSize, sort } = get();
      await get().fetchOrders(organizationId, filters, { page, pageSize, sort });

      return true;
    } catch (error) {
      console.error('Error updating order assignee:', error);
      return false;
    }
  },

  // =====================================================
  // REAL-TIME SUBSCRIPTION
  // =====================================================

  subscribeToOrders: (organizationId: string) => {
    // Un evento realtime ricarica la pagina su cui si e' (filtri, pagina,
    // ordinamento correnti). Prima ogni evento chiamava fetchOrders senza
    // opzioni e riportava l'utente a pagina 1. Le raffiche (import WooCommerce)
    // si raccolgono in una sola ricarica.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const { filters, page, pageSize, sort } = get();
        get().fetchOrders(organizationId, filters, { page, pageSize, sort });
      }, REALTIME_REFRESH_DEBOUNCE_MS);
    };

    // Subscribe to both woocommerce_orders and orders tables
    const wooChannel = supabase
      .channel('woo_orders_unified_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'woocommerce_orders',
          filter: `organization_id=eq.${organizationId}`
        },
        refresh
      )
      .subscribe();

    const manualChannel = supabase
      .channel('manual_orders_unified_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `organization_id=eq.${organizationId}`
        },
        refresh
      )
      .subscribe();

    // Return cleanup function
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(wooChannel);
      supabase.removeChannel(manualChannel);
    };
  },

  // =====================================================
  // RESET
  // =====================================================

  reset: () => {
    set(initialState);
  },

  resetCurrentOrder: () => {
    set({ currentOrder: null });
  }
}));

// =====================================================
// HELPER HOOKS
// =====================================================

/**
 * Get orders filtered by channel
 */
export const useOrdersByChannel = (channel: OrderChannel | 'all') => {
  const orders = useUnifiedOrderStore(state => state.orders);

  if (channel === 'all') return orders;
  return orders.filter(o => o.channel === channel);
};

/**
 * Get order count by status
 */
export const useOrderCountByStatus = (status: string) => {
  const stats = useUnifiedOrderStore(state => state.stats);
  return stats?.byStatus[status] || 0;
};
